import {
  Col,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Radio,
  Row,
  Select,
  SelectProps,
  Tooltip,
  Typography
} from 'antd';
import { Dayjs } from 'dayjs';
import { FormItemCol } from 'components/professor_area/FormItemCol';
import { IClassroomEvaluation } from 'interfaces/classrooms';
import { InfoCircleOutlined } from '@ant-design/icons';
import { handleApiError } from '../../../utils/error-handler';
import { minutesToDays, parseToDayjs, replaceTimeZone } from 'services/date';
import { useForm } from '@refinedev/antd';
import { useUpdate } from '@refinedev/core';

export interface EvaluationModalProps {
  evaluationResource: string;
  resourceMeta?: Record<string, unknown>;
  modalClose: () => void;
  modalProps: ModalProps;
  assignmentSelectorProps: SelectProps<{ value: string; label: string }>;
  assessmentSelectorProps: SelectProps<{ value: string; label: string }>;
  evaluationId: string;
  /** Chamado após salvar alterações com sucesso */
  onUpdateSuccess?: () => void;
}

const EvaluationEditModal = ({
  evaluationResource,
  resourceMeta,
  modalClose,
  modalProps,
  assignmentSelectorProps,
  assessmentSelectorProps,
  evaluationId,
  onUpdateSuccess
}: EvaluationModalProps) => {
  const { mutateAsync: updateEvaluation } = useUpdate({
    mutationOptions: {
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onError: () => {}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any
  });
  const { form, formProps, queryResult } = useForm<IClassroomEvaluation>({
    action: 'edit',
    resource: evaluationResource,
    meta: resourceMeta,
    id: evaluationId
  });
  const type = Form.useWatch('evaluation_type', form);
  const kind = Form.useWatch('kind', form);
  const accessType = Form.useWatch('access_type', form);

  const handleSelectType = () => {
    form.setFieldsValue({ evaluation_id: null });
  };

  const handleInitialData = () => {
    const accessType =
      queryResult?.data?.data?.requirements?.[0]?.rules?.[0]?.kind === 'date'
        ? 'fixed'
        : 'relative';

    let dataRange: (Dayjs | string)[] = [];
    let duration: { start_after_days: number; duration_days: number } = {
      start_after_days: 0,
      duration_days: 0
    };

    if (accessType === 'fixed') {
      dataRange = [
        parseToDayjs(
          queryResult?.data?.data?.requirements?.[0]?.rules?.find(
            rule => rule.operator === 'after'
          )?.value
        ),
        parseToDayjs(
          queryResult?.data?.data?.requirements?.[0]?.rules?.find(
            rule => rule.operator === 'before'
          )?.value
        )
      ];
    } else if (accessType === 'relative') {
      const startAfterDays = minutesToDays(
        Number(
          queryResult?.data?.data?.requirements?.[0]?.rules?.find(
            rule => rule.operator === 'after'
          )?.value
        )
      );
      const durationDays = minutesToDays(
        Number(
          queryResult?.data?.data?.requirements?.[0]?.rules?.find(
            rule => rule.operator === 'before'
          )?.value
        )
      );

      duration = {
        start_after_days: startAfterDays,
        duration_days: durationDays - startAfterDays
      };
    }

    return {
      kind: queryResult?.data?.data?.kind,
      access_type: accessType,
      dateRange: dataRange,
      duration_days: duration?.duration_days,
      start_after_days: duration?.start_after_days
    };
  };

  const handleUpdateEvaluation = async () => {
    try {
      await form.validateFields();

      const [starts_at, ends_at] = form.getFieldValue('dateRange');
      const isFinal = form.getFieldValue('kind');

      await updateEvaluation({
        resource: evaluationResource,
        meta: resourceMeta,
        values: {
          ...form.getFieldsValue(),
          starts_at: replaceTimeZone(starts_at),
          ends_at: replaceTimeZone(ends_at),
          kind: isFinal ? 'final' : 'regular'
        },
        id: evaluationId
      });

      await queryResult?.refetch().then(response => {
        formProps.initialValues = { ...response.data?.data };
      });

      onUpdateSuccess?.();
      modalClose();
    } catch (error) {
      handleApiError(error);
    }
  };

  return (
    <Modal
      {...modalProps}
      title="Editar avaliação"
      cancelText="Cancelar"
      confirmLoading={modalProps.confirmLoading}
      okText="Editar avaliação"
      className="!w-[1022px]"
      onOk={handleUpdateEvaluation}
      destroyOnClose
    >
      <Form
        form={form}
        {...formProps}
        initialValues={{
          ...queryResult?.data?.data,
          ...handleInitialData()
        }}
        layout="vertical"
      >
        <Typography.Title level={5}>
          Informações gerais da avaliação
        </Typography.Title>

        <Row gutter={[16, 16]}>
          <Col md={8}>
            <Form.Item
              label="Tipo"
              name="evaluation_type"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <Select onChange={handleSelectType} disabled>
                <Select.Option value="Assessment">Avaliação</Select.Option>
                <Select.Option value="Assignment">Atividade</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col md={8}>
            <Form.Item
              label="Avaliação"
              name="evaluation_id"
              rules={[
                {
                  required: true
                }
              ]}
            >
              {type === 'Assessment' ? (
                <Select
                  notFoundContent={
                    <Empty description="Não há avaliações cadastradas para essa disciplina" />
                  }
                  disabled
                  {...assessmentSelectorProps}
                  style={{ minWidth: 200 }}
                />
              ) : (
                <Select
                  notFoundContent={
                    <Empty description="Não há atividades cadastradas para essa disciplina" />
                  }
                  disabled
                  {...assignmentSelectorProps}
                  style={{ minWidth: 200 }}
                />
              )}
            </Form.Item>
          </Col>
          <Col md={8}>
            <Form.Item
              label="Nome"
              name="name"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <Input style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          {!kind && (
            <Col md={8}>
              <Form.Item
                label="Peso"
                name="weight"
                rules={[
                  {
                    required: true
                  }
                ]}
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          )}
          <Col md={8}>
            <Form.Item
              label="Nota mínima (0 - 100)"
              name="passing_score"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col md={8}>
            <Form.Item
              label="Tentativas"
              name="max_attempts"
              rules={[
                {
                  required: true
                }
              ]}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row>
          <Typography.Title className="align-middle gap-2 flex" level={5}>
            Período de acessos
            <Tooltip
              placement="rightBottom"
              title="O período de acesso de uma avaliação deve estar entre a data de início e fim da turma"
            >
              <InfoCircleOutlined />
            </Tooltip>
          </Typography.Title>
        </Row>

        <Row>
          <Col span={24} style={{ marginBottom: 8 }}>
            <Form.Item
              name="access_type"
              initialValue="fixed"
              help={
                accessType === 'fixed' || !accessType
                  ? 'O acesso ocorre em uma data de início específica, independente da data de matrícula do aluno'
                  : 'O acesso ocorre a partir de um número de dias após a matrícula do aluno'
              }
              style={{ marginBottom: 0 }}
            >
              <Radio.Group buttonStyle="outline" className="mb-2">
                <Radio.Button value="fixed">Data fixa</Radio.Button>
                <Radio.Button value="relative">Data relativa</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {accessType === 'fixed' && (
          <>
            <FormItemCol
              label="Início"
              name={['dateRange', 0]}
              gutter={[0, 0]}
              rules={[{ required: true, message: 'Informe a data de início' }]}
            >
              <DatePicker showTime format="DD/MM/YYYY HH:mm" className="mb-0" />
            </FormItemCol>
            <FormItemCol
              label="Término"
              name={['dateRange', 1]}
              gutter={[0, 0]}
              rules={[{ required: true, message: 'Informe a data de término' }]}
            >
              <DatePicker showTime format="DD/MM/YYYY HH:mm" />
            </FormItemCol>
          </>
        )}

        {accessType === 'relative' && (
          <>
            <FormItemCol
              label="Dias para início"
              name="start_after_days"
              rules={[{ required: true }]}
              cols={[4, 3, 10]}
              help="dias após a matrícula do aluno."
            >
              <InputNumber className="w-full" />
            </FormItemCol>
            <FormItemCol
              label="Duração (dias)"
              name="duration_days"
              rules={[{ required: true }]}
              cols={[4, 3, 10]}
              help="dias após o início da avaliação."
            >
              <InputNumber className="w-full" />
            </FormItemCol>
          </>
        )}

        {/* Tipo de avaliação */}
        <Row align="middle" gutter={[16, 16]} className="mb-2">
          <Col md={16}>
            <Form.Item
              label="Tipo de avaliação"
              help={
                'Avaliação de recuperação deve acontecer após o termino de todas as outras avaliações'
              }
              name="kind"
              style={{ marginBottom: 0 }}
            >
              <Radio.Group buttonStyle="outline" className="mb-2" disabled>
                <Radio.Button value="regular">Avaliação padrão</Radio.Button>
                <Radio.Button value="final">
                  Avaliação de recuperação
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default EvaluationEditModal;
